import React, { useContext } from 'react'
import View from '../../atoms/View'
import Text from '../../atoms/TextNew'
import { ItemContext, ItemDispatchContext } from 'src/contexts/itemContext'
import { useTranslation } from 'react-i18next'
import Space from 'src/components/atoms/Space'
import DownloadPdfSvg from 'src/components/other/svg/DownloadPdfSvg'
import Button from 'src/components/atoms/Button'
import InRealizationSvg from 'src/components/other/svg/InRealizationSvg'
import ActionBar from './ActionBar'
import { formatPrice } from 'src/helpers/formatPrice'

const ExpandedAccepted = ({
  expandedView,
  variant
}: {
  expandedView: boolean
  variant: 'realization' | 'archived'
}): JSX.Element => {
  const itemContext = useContext(ItemContext)
  const dispatchItemContext = useContext(ItemDispatchContext)
  const { t } = useTranslation()

  if (itemContext === null || dispatchItemContext === null) {
    throw new Error('itemContext must be used within a ItemProvider')
  }

  const { realization_details } = itemContext
  const showTransportIcon = realization_details?.easy_transport_enabled
  const creditDetailsExists = typeof realization_details.credit_bank === 'string'
  const { id } = itemContext

  return (
    <View
      style={{
        height: expandedView ? 260 : 0,
        overflow: 'hidden',
        transition: 'height 500ms ease'
      }}>
      <View
        className="grid grid-cols-6 gap-10 pl-8 pb-4 cursor-default"
        style={{
          gridTemplateColumns: creditDetailsExists ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)'
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}>
        <View>
          <Space y={4} />
          <Text className="text-xl">{t('in_realization_list_item.contact_info')}</Text>
          <Space y={1} />
          <Text data-testid="seller-name" className="text-darkGrey text-l font-SemiBold">
            {realization_details.seller_fist_name} {realization_details.seller_last_name}
          </Text>
          <Text data-testid="seller-street" className="text-darkGrey text-l">
            {[realization_details.street, realization_details.building_number].filter(Boolean).join(' ')}
          </Text>
          <Text className="text-darkGrey text-l">
            {realization_details.zip_code} {realization_details.place}
          </Text>
          {realization_details.deregistering_person !== null && (
            <>
              <Space y={2} />
              <Text className="text-xl">{t('in_realization_list_item.deregistering_person')}</Text>
              <Text className="text-darkGrey text-l font-SemiBold">
                {t(`in_realization_list_item.deregistering_person_${realization_details.deregistering_person}`)}
              </Text>
            </>
          )}
        </View>
        {creditDetailsExists && (
          <View>
            <Space y={4} />
            <Text className="text-xl">{t('in_realization_list_item.credit_data')}</Text>
            <Space y={1} />
            <Text className="text-darkGrey text-l">{realization_details.credit_bank}</Text>
            <Text className="text-darkGrey text-l">{realization_details.credit_number ?? ''}</Text>
          </View>
        )}

        <View>
          <Space y={4} />
          <Text className="text-xl">{t('in_realization_list_item.email')}</Text>
          <Text className="text-darkGrey text-l font-SemiBold">{realization_details.seller_email}</Text>
          <Space y={2} />
          <Text className="text-xl">{t('in_realization_list_item.phone')}</Text>
          <Text className="text-darkGrey text-l font-SemiBold">{realization_details.phone}</Text>
        </View>
        {realization_details.client_price !== null && (
          <View>
            <Space y={4} />
            <Text className="text-xl">{t('in_realization_list_item.price')}</Text>
            <Text className="text-darkGrey text-l font-SemiBold">
              {formatPrice(realization_details.client_price ?? 0)} €
            </Text>
            <Space y={2} />
            <Text className="text-xl">{t('in_realization_list_item.provision')}</Text>
            <Text className="text-darkGrey text-l font-SemiBold">
              {formatPrice(realization_details.commission ?? 0)} €
            </Text>
          </View>
        )}
        <View className="flex flex-col justify-between pr-2 pt-2">
          {typeof realization_details.vehicle_data_pdf === 'string' && (
            <View
              className="flex gap-3 items-center cursor-pointer"
              onClick={() => {
                window.location.href = realization_details.vehicle_data_pdf ?? ''
              }}>
              <DownloadPdfSvg className="w-11 h-11" svgStyles="w-11 h-11" />
              <Text className="text-xl text-center">{t('in_realization_list_item.vehicle_data')}</Text>
            </View>
          )}
          {typeof realization_details.contract_pdf === 'string' && (
            <View
              className="flex gap-3 items-center cursor-pointer"
              onClick={() => {
                window.location.href = realization_details.contract_pdf ?? ''
              }}>
              <DownloadPdfSvg className="w-11 h-11" svgStyles="w-11 h-11" />
              <Text className="text-xl text-center">{t('in_realization_list_item.contract_pdf')}</Text>
            </View>
          )}
        </View>
        <View>
          {showTransportIcon && variant === 'realization' && (
            <View className="flex items-end justify-end h-full mr-5">
              {realization_details?.spedition_arranged ? (
                <Button
                  variant="smallWithIcon"
                  customStyles="flex items-center gap-2 text-white bg-grey"
                  color="grey"
                  disabled={true}
                  iconComponent={<InRealizationSvg svgStyles="w-8 h-8" />}
                  onClick={() => {
                    // do nothing
                  }}>
                  {t('in_realization_list_item.transport_arranged')}
                </Button>
              ) : (
                <Button
                  variant="smallWithIcon"
                  customStyles="flex items-center gap-2 text-white"
                  color="darkBlue"
                  iconComponent={<InRealizationSvg svgStyles="w-8 h-8" />}
                  onClick={() => {
                    dispatchItemContext({ type: 'SET_BAR', payload: 'transport' })
                  }}>
                  {t('in_realization_list_item.arrange_transport')}
                </Button>
              )}
            </View>
          )}
        </View>
      </View>
      {variant === 'realization' && <ActionBar offerId={id} />}
    </View>
  )
}

export default ExpandedAccepted
